import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha } from "@/lib/fechas"

import { TONO_ESTADO } from "./tipos"

/**
 * La portada de un documento controlado: lo que lo hace controlado.
 *
 * ⚠ **Las tres firmas van SIEMPRE, aunque estén vacías.** Un documento del SGI sin
 * quién lo elaboró, quién lo revisó y quién lo aprobó no está controlado, y esconder
 * las casillas vacías hace que nadie note que faltan. Se pinta el hueco con una raya,
 * que es lo que hace un formato en papel.
 *
 * ⚠ **La próxima revisión se enseña aunque no haya pasado.** Es la mitad de para qué
 * existe el ciclo: un procedimiento que nadie revisa desde hace tres años sigue
 * diciendo VIGENTE, y la única señal de que no lo está es esa fecha.
 */

type Datos = {
  codigo: string
  titulo: string
  subtitulo?: string | null
  clasificacion?: string | null
  version: string
  estado: string
  fecha_emision?: string | null
  proxima_revision?: string | null
  elaborado_por?: string | null
  revisado_por?: string | null
  aprobado_por?: string | null
  relacionados?: string[]
}

function Firma({ rol, quien }: { rol: string; quien: string | null | undefined }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
        {rol}
      </p>
      <p className="mt-1 border-t pt-1 text-sm" style={{ borderColor: "var(--border)" }}>
        {quien || "—"}
      </p>
    </div>
  )
}

export function PortadaDocumento({ doc }: { doc: Datos }) {
  const tono = TONO_ESTADO[doc.estado as keyof typeof TONO_ESTADO] ?? "neutro"
  return (
    <header className="mb-8 superficie p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className="font-mono text-xs font-semibold tabular"
            style={{ color: "var(--primary-texto)" }}
          >
            {doc.codigo}
          </p>
          <h1 className="mt-1 text-xl font-bold">{doc.titulo}</h1>
          {doc.subtitulo && (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              {doc.subtitulo}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Insignia tono={tono}>{doc.estado}</Insignia>
          <Insignia tono="info">v{doc.version}</Insignia>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-[11px] font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
            Emisión
          </dt>
          <dd className="tabular">{doc.fecha_emision ? comoFecha(doc.fecha_emision) : "—"}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
            Próxima revisión
          </dt>
          {/* Texto, no fecha: lo que los documentos escriben ahí es una politica
              —«Anual o ante cambio relevante»—, y formatearlo como dia la perderia. */}
          <dd>{doc.proxima_revision || "—"}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
            Clasificación
          </dt>
          <dd>{doc.clasificacion || "—"}</dd>
        </div>
        <div>
          <dt className="text-[11px] font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
            Documentos relacionados
          </dt>
          <dd>{doc.relacionados?.length ? doc.relacionados.join(" · ") : "—"}</dd>
        </div>
      </dl>

      <div
        className="mt-5 grid grid-cols-1 gap-5 border-t pt-4 sm:grid-cols-3"
        style={{ borderColor: "var(--border)" }}
      >
        <Firma rol="Elaborado por" quien={doc.elaborado_por} />
        <Firma rol="Revisado por" quien={doc.revisado_por} />
        <Firma rol="Aprobado por" quien={doc.aprobado_por} />
      </div>
    </header>
  )
}
